import {
  addMonths,
  endOfMonth,
  epochDayOf,
  plainDateFromEpochDay,
  startOfMonth,
} from '../../../core/services/plain-date';

import type { GanttConfig, Row } from '../types';

export type DayRange = { startDay: number; endDay: number };

export const DEFAULT_RANGE_OPTIONS = {
  padMonths: 6,
  extendMonths: 6,
  /** Backstop against the old module's unbounded growth on repeated edge scrolls. */
  maxSpanYears: 20,
} as const;

/**
 * Every field of `GanttConfig['range']` is optional, so a consumer can pass an explicit
 * `undefined` (e.g. `range={{ padMonths: props.padMonths }}`) without TypeScript objecting —
 * `strictNullChecks` is off for this app. A plain object spread lets that explicit `undefined`
 * win over the default, so each field falls back individually with `??` instead.
 */
const resolve = (options?: GanttConfig['range']) => ({
  padMonths: options?.padMonths ?? DEFAULT_RANGE_OPTIONS.padMonths,
  extendMonths: options?.extendMonths ?? DEFAULT_RANGE_OPTIONS.extendMonths,
  maxSpanYears: options?.maxSpanYears ?? DEFAULT_RANGE_OPTIONS.maxSpanYears,
});

const monthStartDay = (epochDay: number, monthsOffset: number): number =>
  epochDayOf(startOfMonth(addMonths(plainDateFromEpochDay(epochDay), monthsOffset)));

const monthEndDay = (epochDay: number, monthsOffset: number): number =>
  epochDayOf(endOfMonth(addMonths(plainDateFromEpochDay(epochDay), monthsOffset)));

/**
 * Whole months covering every dated row and today, padded on both sides. Today is always in
 * range so the "today" marker and the initial auto-scroll always have somewhere to land.
 */
export const initialRange = (
  rows: Row[],
  today: number,
  options?: GanttConfig['range'],
): DayRange => {
  const { padMonths } = resolve(options);
  let minDay = today;
  let maxDay = today;

  rows.forEach((row) => {
    if (row.span) {
      if (row.span.startDay < minDay) minDay = row.span.startDay;
      if (row.span.endDay > maxDay) maxDay = row.span.endDay;
    }
  });

  return {
    startDay: monthStartDay(minDay, -padMonths),
    endDay: monthEndDay(maxDay, padMonths),
  };
};

export const extendRange = (
  range: DayRange,
  side: 'start' | 'end',
  options?: GanttConfig['range'],
): DayRange => {
  const { extendMonths, maxSpanYears } = resolve(options);

  if (range.endDay - range.startDay >= maxSpanYears * 365) return range;

  return side === 'start'
    ? { ...range, startDay: monthStartDay(range.startDay, -extendMonths) }
    : { ...range, endDay: monthEndDay(range.endDay, extendMonths) };
};
